import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Poll, PollInsert, PollUpdate, PollSubmissionData } from '@/types/poll';
import { useToast } from '@/hooks/use-toast';
import { useBrandManager } from '@/hooks/useBrandManager';

// Fetch all polls for a brand
export const usePolls = (brandId?: string) => {
  return useQuery({
    queryKey: ['polls', brandId],
    queryFn: async () => {
      let query = supabase
        .from('os_polls')
        .select('*')
        .order('created_at', { ascending: false });

      if (brandId) {
        query = query.eq('brand_id', brandId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Poll[];
    },
    enabled: !!brandId,
  });
};

// Fetch single poll by ID
export const usePoll = (pollId?: string) => {
  return useQuery({
    queryKey: ['poll', pollId],
    queryFn: async () => {
      if (!pollId) throw new Error('Poll ID is required');

      const { data, error } = await supabase
        .from('os_polls')
        .select('*')
        .eq('id', pollId)
        .single();

      if (error) throw error;
      return data as Poll;
    },
    enabled: !!pollId,
  });
};

// Fetch poll by share code (for public access)
export const usePollByShareCode = (shareCode?: string) => {
  return useQuery({
    queryKey: ['poll', 'share', shareCode],
    queryFn: async () => {
      if (!shareCode) throw new Error('Share code is required');

      const { data, error } = await supabase
        .from('os_polls')
        .select('*')
        .eq('share_code', shareCode)
        .eq('is_published', true)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Poll not found or not published');
      if (!data.brand_id) throw new Error('Poll missing brand information');
      return data as Poll;
    },
    enabled: !!shareCode,
  });
};

// Create poll
export const useCreatePoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (poll: PollInsert) => {
      const { data, error } = await supabase
        .from('os_polls')
        .insert(poll)
        .select()
        .single();

      if (error) throw error;
      return data as Poll;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      toast({
        title: 'Success',
        description: 'Poll created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Update poll
export const useUpdatePoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PollUpdate }) => {
      const { data, error } = await supabase
        .from('os_polls')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Poll;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      queryClient.invalidateQueries({ queryKey: ['poll', data.id] });
      toast({
        title: 'Success',
        description: 'Poll updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const usePollSubmissions = (pollId: string) => {
  return useQuery({
    queryKey: ['poll-submissions', pollId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_poll_submissions')
        .select('*')
        .eq('poll_id', pollId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!pollId,
  });
};

// Delete poll
export const useDeletePoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pollId: string) => {
      const { error } = await supabase
        .from('os_polls')
        .delete()
        .eq('id', pollId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      toast({
        title: 'Success',
        description: 'Poll deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Publish poll (generate share code)
export const usePublishPoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pollId: string) => {
      // Generate share code
      const shareCode = Math.random().toString(36).substring(2, 12);
      
      const { data, error } = await supabase
        .from('os_polls')
        .update({ 
          is_published: true, 
          share_code: shareCode 
        })
        .eq('id', pollId)
        .select()
        .single();

      if (error) throw error;
      return data as Poll;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      queryClient.invalidateQueries({ queryKey: ['poll', data.id] });
      toast({
        title: 'Success',
        description: 'Poll published successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Unpublish poll
export const useUnpublishPoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pollId: string) => {
      const { data, error } = await supabase
        .from('os_polls')
        .update({ is_published: false })
        .eq('id', pollId)
        .select()
        .single();

      if (error) throw error;
      return data as Poll;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      queryClient.invalidateQueries({ queryKey: ['poll', data.id] });
      toast({
        title: 'Success',
        description: 'Poll unpublished successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Duplicate poll
export const useDuplicatePoll = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pollId: string) => {
      // Fetch the original poll
      const { data: originalPoll, error: fetchError } = await supabase
        .from('os_polls')
        .select('*')
        .eq('id', pollId)
        .single();

      if (fetchError) throw fetchError;
      if (!originalPoll) throw new Error('Poll not found');

      // Create a duplicate with modified name
      const { id, created_at, updated_at, share_code, is_published, ...pollData } = originalPoll;
      
      const { data, error } = await supabase
        .from('os_polls')
        .insert({
          ...pollData,
          name: `${originalPoll.name} (Copy)`,
          is_published: false,
          share_code: null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['polls'] });
      toast({
        title: 'Success',
        description: 'Poll duplicated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Submit poll response
export const usePollSubmission = () => {
  const { toast } = useToast();
  const { currentBrandId } = useBrandManager();

  return useMutation({
    mutationFn: async (submission: PollSubmissionData) => {
      // Call the secure server-side RPC function
      const { data, error } = await supabase.rpc('submit_poll_response', {
        p_poll_id: submission.pollId,
        p_name: (submission.name || '').toString(),
        p_email: (submission.email || '').toString(),
        p_display_name: (submission.displayName || '').toString(),
        p_anonymous: !!submission.anonymous,
        p_responses: submission.responses as any,
        p_completion_time_seconds: submission.completionTimeSeconds || 0,
        p_metadata: {
          completed_at: submission.completedAt,
          poll_name: submission.pollName,
        },
      });

      if (error) throw error;

      // Database function handles incoming_business_metrics insertion
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Your response has been submitted',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Fetch poll leaderboard with real-time updates
export const usePollLeaderboard = (pollId: string, limit = 10) => {
  return useQuery({
    queryKey: ['poll-leaderboard', pollId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_poll_leaderboard')
        .select('*')
        .eq('poll_id', pollId)
        .order('rank', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data;
    },
    enabled: !!pollId,
    refetchInterval: 15000, // Refetch every 15 seconds (reduced from 5s to prevent rate limiting)
  });
};
